import { Button } from '@chiroup/components/Button';
import { ColorPicker } from '@chiroup/components/color-picker/ColorPicker';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Textarea } from '@chiroup/components/form/Textarea';
import {
  CUSTOM_HTML_EXAMPLE,
  DEFAULT_LOGIN_CONTENT,
  LoginContent,
  LoginContentLayout,
  LoginContentVariantKey,
} from '@chiroup/core/types/LoginContent.type';
import { useEffect, useState } from 'react';
import appDataService from '../../services/app-data.service';
import Uploader from '../common/uploader/Uploader';
import Header from '../layout/Header';
import LoginContentPreview from './LoginContentPreview';

const VARIANT_TABS: { key: LoginContentVariantKey; label: string }[] = [
  { key: 'ehr', label: 'EHR' },
  { key: 'essentials', label: 'Essentials' },
  { key: 'unknown', label: 'Unknown / New User' },
  { key: 'test', label: 'Test (Clinic 27)' },
];

const LAYOUT_OPTIONS = [
  { value: 'none', text: 'None (login only)' },
  { value: 'classic', text: 'Classic Split' },
  { value: 'card', text: 'Card-in-Panel' },
  { value: 'full-bleed', text: 'Full-Bleed Image' },
  { value: 'showcase', text: 'Showcase (SF-style)' },
  { value: 'custom', text: 'Custom HTML' },
];

const LoginContentEditor = () => {
  const [data, setData] = useState<LoginContent>(DEFAULT_LOGIN_CONTENT);
  const [activeTab, setActiveTab] = useState<LoginContentVariantKey>('ehr');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let isDestroyed = false;

    const loadData = async () => {
      try {
        const res = await appDataService.get('loginContent');

        if (!isDestroyed && res) {
          setData({
            ...DEFAULT_LOGIN_CONTENT,
            ...res,
          });
        }
      } catch {
        // Key doesn't exist yet — use defaults
      }
    };

    loadData();

    return () => {
      isDestroyed = true;
    };
  }, []);

  const save = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await appDataService.update('loginContent', data);
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const variant = data[activeTab];
  const isNone = variant.layout === 'none';
  const isCustom = variant.layout === 'custom';

  const updateField = (field: string, value: string) => {
    setData(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: value,
      },
    }));
  };

  const insertExample = () => {
    updateField('customHtml', CUSTOM_HTML_EXAMPLE);
  };

  return (
    <>
      <Header
        title="Login Screen Content"
        rightSide={
          <div className="flex items-center gap-3">
            {saveMessage && (
              <span
                className={`text-sm ${saveMessage === 'Saved!' ? 'text-green-600' : 'text-red-600'}`}
              >
                {saveMessage}
              </span>
            )}
            <Button text="Save" onClick={save} loading={isSaving} />
          </div>
        }
      />
      <div className="p-6">
        {/* Variant Tabs */}
        <div className="border-b border-gray-200 dark:border-dark-gray-700 mb-6">
          <nav className="-mb-px flex gap-6">
            {VARIANT_TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Form */}
          <div className="space-y-4">
            <Select
              name="layout"
              label="Layout Style"
              value={variant.layout}
              onChange={val => updateField('layout', val as LoginContentLayout)}
              options={LAYOUT_OPTIONS}
            />

            {isNone ? (
              <p className="text-sm text-gray-400 py-4">
                Standard login screen — no marketing panel will be shown.
              </p>
            ) : isCustom ? (
              <>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-gray-300">
                    HTML
                  </label>
                  <button
                    type="button"
                    onClick={insertExample}
                    className="text-xs text-primary-500 hover:text-primary-600 font-medium"
                  >
                    Insert example
                  </button>
                </div>
                <textarea
                  value={variant.customHtml || ''}
                  onChange={e => updateField('customHtml', e.target.value)}
                  rows={18}
                  spellCheck={false}
                  className="w-full font-mono text-xs p-3 rounded-lg border border-gray-300 dark:border-dark-gray-600 bg-gray-50 dark:bg-dark-gray-800 text-gray-900 dark:text-dark-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder='<div class="flex flex-1 ...">&#10;  Your HTML here — Tailwind classes are available.&#10;</div>'
                />
                <p className="text-xs text-gray-400">
                  Write HTML with inline{' '}
                  <code className="bg-gray-100 dark:bg-dark-gray-700 px-1 rounded text-[11px]">
                    {'<style>'}
                  </code>{' '}
                  tags for styling. The outer container should use{' '}
                  <code className="bg-gray-100 dark:bg-dark-gray-700 px-1 rounded text-[11px]">
                    display:flex; flex:1
                  </code>{' '}
                  to fill the panel. Click &quot;Insert example&quot; to see a working template.
                </p>
              </>
            ) : (
              <>
                <Uploader
                  name="image"
                  label="Image"
                  className=""
                  onChange={val => updateField('image', val ?? '')}
                  value={variant.image}
                  type="image"
                  uploadCategory="loginContent"
                  uploadType="image"
                  fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
                  fileSizeLimit={10}
                />
                <Input
                  name="title"
                  label="Title"
                  value={variant.title}
                  onChange={val => updateField('title', val)}
                />
                <Input
                  name="subtitle"
                  label="Subtitle"
                  value={variant.subtitle}
                  onChange={val => updateField('subtitle', val)}
                />
                <Textarea
                  name="content"
                  label="Content"
                  value={variant.content}
                  onChange={val => updateField('content', val)}
                  rows={4}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    name="buttonText"
                    label="Button Text"
                    value={variant.buttonText}
                    onChange={val => updateField('buttonText', val)}
                  />
                  <Input
                    name="buttonLink"
                    label="Button Link"
                    value={variant.buttonLink}
                    onChange={val => updateField('buttonLink', val)}
                  />
                </div>
                <div className="flex items-center gap-6 pt-2">
                  <ColorPicker
                    label="Background Color"
                    value={variant.backgroundColor}
                    onChange={val => updateField('backgroundColor', val)}
                  />
                  <ColorPicker
                    label="Accent Color"
                    value={variant.accentColor}
                    onChange={val => updateField('accentColor', val)}
                  />
                  <Checkbox
                    name="textCard"
                    label="Text backdrop"
                    value={!!variant.textCard}
                    onChange={val =>
                      setData(prev => ({
                        ...prev,
                        [activeTab]: { ...prev[activeTab], textCard: val },
                      }))
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* Preview */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
              Preview
            </div>
            <LoginContentPreview variant={variant} />
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginContentEditor;
